import {
  createDevice,
  createDeviceServiceRecord,
  deleteDevice,
  getDeviceById,
  getDevicePhotoBlob,
  getDeviceAccessSummary,
  listDeviceServiceRecords,
  listDeviceServiceRecordPlatformUsers,
  listDeviceLocationSuggestions,
  listDeviceAssignedUserSuggestions,
  getMasterViewSnapshot,
  listDevicesPage,
  updateDeviceServiceRecord,
  updateDevice,
  updateDeviceAssignments,
  uploadDevicePhoto,
  type ListDevicesPageParams,
} from "@/lib/api/endpoints/devices";
import {
  createDeviceChannel,
  deleteDeviceChannel,
  listDeviceChannels,
  updateDeviceChannel,
} from "@/lib/api/endpoints/device-channels";
import {
  getOtaHistory,
  getOtaStatus,
  postOtaRollback,
  postOtaSchedule,
  postOtaUpdate,
} from "@/lib/api/endpoints/ota";
import type {
  DeviceChannelCreateDto,
  DeviceChannelDto,
  DeviceCreateDto,
  DeviceDto,
  DeviceLocationSuggestionDto,
  DeviceLocationSuggestionPageDto,
  DeviceAssignedUserSuggestionPageDto,
  DevicePageDto,
  DeviceServiceRecordCreateDto,
  DeviceServiceRecordDto,
  DeviceServiceRecordPlatformUserDto,
  DeviceServiceRecordPlatformUserPageDto,
  DeviceServiceRecordUpdateDto,
  DeviceUpdateDto,
  MasterViewSnapshotPageDto,
} from "@/lib/api/types";
import type {
  OtaHistoryEntryDto,
  OtaScheduleRequestDto,
  OtaStatusDto,
  OtaUpdateRequestDto,
} from "@/lib/api/types/ota";

export type DeviceListFilters = {
  search: string;
  /** Empty string = all */
  status: string;
};

export const deviceQueryKeys = {
  all: ["devices"] as const,
  list: (tenantId: string, filters: DeviceListFilters) =>
    [...deviceQueryKeys.all, "list", tenantId, filters.search, filters.status] as const,
  managementList: (
    tenantId: string,
    search: string,
    status: string,
    statusScope: string,
    protocol: string,
    locationKind: string,
    locationValue: string,
    simType: string,
    assignmentState: string,
    assignedUserId: string,
    warrantyScope: string,
    deviceCategoryId: string,
    alertPreset: string,
    alertRangeKey: string,
    alertStart: string,
    alertEnd: string,
    page: number,
    pageSize: number,
    sort: string | null,
    order: string,
    workspaceScope: string,
  ) =>
    [
      ...deviceQueryKeys.all,
      "mgmt",
      tenantId,
      search,
      status,
      statusScope,
      protocol,
      locationKind,
      locationValue,
      simType,
      assignmentState,
      assignedUserId,
      warrantyScope,
      deviceCategoryId,
      alertPreset,
      alertRangeKey,
      alertStart,
      alertEnd,
      page,
      pageSize,
      sort,
      order,
      workspaceScope,
    ] as const,
  detail: (deviceId: string) => [...deviceQueryKeys.all, "detail", deviceId] as const,
  serviceRecords: (deviceId: string) => [...deviceQueryKeys.detail(deviceId), "service-records"] as const,
  serviceRecordPlatformUsers: (deviceId: string) =>
    [...deviceQueryKeys.detail(deviceId), "service-record-platform-users"] as const,
  otaStatus: (deviceId: string) => [...deviceQueryKeys.all, "ota", "status", deviceId] as const,
  otaHistory: (deviceId: string) => [...deviceQueryKeys.all, "ota", "history", deviceId] as const,
};

export const devicesGateway = {
  listPage(params: ListDevicesPageParams): Promise<DevicePageDto> {
    return listDevicesPage(params);
  },
  listLocationSuggestions(params: {
    tenantId?: string | null;
    workspaceScope?: "all" | null;
    q?: string | null;
    limit?: number;
    offset?: number;
  }): Promise<DeviceLocationSuggestionPageDto> {
    return listDeviceLocationSuggestions(params);
  },
  listAssignedUserSuggestions(params: {
    tenantId?: string | null;
    workspaceScope?: "all" | null;
    q?: string | null;
    limit?: number;
    offset?: number;
  }): Promise<DeviceAssignedUserSuggestionPageDto> {
    return listDeviceAssignedUserSuggestions(params);
  },
  getById(deviceId: string): Promise<DeviceDto> {
    return getDeviceById(deviceId);
  },
  create(body: DeviceCreateDto): Promise<DeviceDto> {
    return createDevice(body);
  },
  update(deviceId: string, body: DeviceUpdateDto): Promise<DeviceDto> {
    return updateDevice(deviceId, body);
  },
  remove(deviceId: string): Promise<void> {
    return deleteDevice(deviceId);
  },
  accessSummary(): Promise<{ assigned_count: number }> {
    return getDeviceAccessSummary();
  },
  getMasterViewSnapshot(deviceIds: string[]): Promise<MasterViewSnapshotPageDto> {
    return getMasterViewSnapshot(deviceIds);
  },
  listServiceRecords(deviceId: string): Promise<DeviceServiceRecordDto[]> {
    return listDeviceServiceRecords(deviceId);
  },
  createServiceRecord(deviceId: string, body: DeviceServiceRecordCreateDto): Promise<DeviceServiceRecordDto> {
    return createDeviceServiceRecord(deviceId, body);
  },
  updateServiceRecord(
    deviceId: string,
    recordId: string,
    body: DeviceServiceRecordUpdateDto,
  ): Promise<DeviceServiceRecordDto> {
    return updateDeviceServiceRecord(deviceId, recordId, body);
  },
  listServiceRecordPlatformUsers(deviceId: string): Promise<DeviceServiceRecordPlatformUserPageDto> {
    return listDeviceServiceRecordPlatformUsers(deviceId);
  },
  updateAssignments(deviceId: string, userIds: string[], tenantId?: string | null): Promise<DeviceDto> {
    return updateDeviceAssignments(deviceId, userIds, tenantId);
  },
  uploadPhoto(file: File): Promise<{ path: string }> {
    return uploadDevicePhoto(file);
  },
  getPhotoBlob(photoPath: string): Promise<Blob> {
    return getDevicePhotoBlob(photoPath);
  },
};

export const deviceChannelsGateway = {
  list(deviceId: string): Promise<DeviceChannelDto[]> {
    return listDeviceChannels(deviceId);
  },
  create(deviceId: string, body: DeviceChannelCreateDto): Promise<DeviceChannelDto> {
    return createDeviceChannel(deviceId, body);
  },
  update(channelId: string, body: Partial<DeviceChannelDto>): Promise<DeviceChannelDto> {
    return updateDeviceChannel(channelId, body);
  },
  remove(channelId: string): Promise<void> {
    return deleteDeviceChannel(channelId);
  },
};

export const otaGateway = {
  getStatus(deviceId: string): Promise<OtaStatusDto> {
    return getOtaStatus(deviceId);
  },
  getHistory(deviceId: string): Promise<OtaHistoryEntryDto[]> {
    return getOtaHistory(deviceId);
  },
  postUpdate(deviceId: string, body: OtaUpdateRequestDto): Promise<OtaHistoryEntryDto> {
    return postOtaUpdate(deviceId, body);
  },
  postSchedule(deviceId: string, body: OtaScheduleRequestDto): Promise<OtaHistoryEntryDto> {
    return postOtaSchedule(deviceId, body);
  },
  postRollback(deviceId: string): Promise<OtaHistoryEntryDto> {
    return postOtaRollback(deviceId);
  },
};

export type {
  DeviceChannelCreateDto,
  DeviceChannelDto,
  DeviceCreateDto,
  DeviceDto,
  DeviceLocationSuggestionDto,
  DeviceLocationSuggestionPageDto,
  DevicePageDto,
  DeviceServiceRecordCreateDto,
  DeviceServiceRecordDto,
  DeviceServiceRecordPlatformUserDto,
  DeviceServiceRecordPlatformUserPageDto,
  DeviceServiceRecordUpdateDto,
  DeviceUpdateDto,
  ListDevicesPageParams,
};
export type {
  OtaHistoryEntryDto,
  OtaScheduleRequestDto,
  OtaStatusDto,
  OtaUpdateRequestDto,
} from "@/lib/api/types/ota";
